import React, { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';
import MarkdownRenderer from './MarkdownRenderer';
import LoadingScreen from './LoadingScreen';
import { WarningIcon } from './Icons';
import styles from '../styles/DetailView.module.css';

/**
 * Article Detail View Component
 * Displays full article content with markdown rendering
 *
 * Props:
 * - slug: Article slug to load
 * - onBack: Callback when back button is clicked
 */
function ArticleDetailView({ slug, onBack }) {
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadArticle() {
      if (!slug) {
        setError('No article specified');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await contentAPI.getArticle(slug);
        setArticle(data);
      } catch (err) {
        console.error('[ArticleDetailView] Error loading article:', err);
        setError(err.message || 'Failed to load article');
      } finally {
        setIsLoading(false);
      }
    }

    loadArticle();
  }, [slug]);

  // Format category display
  const getCategoryDisplay = (category) => {
    if (!category) return 'Article';

    const categories = {
      education: 'Education',
      investing: 'Investing',
      personal: 'Personal Finance',
      news: 'News'
    };

    return categories[category.toLowerCase()] || category;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <LoadingScreen text="Loading article..." variant="dots" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            <WarningIcon size={64} />
          </div>
          <div className={styles.errorText}>{error}</div>
          <button onClick={onBack} className={styles.errorButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Article not found
  if (!article) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            <WarningIcon size={64} />
          </div>
          <div className={styles.errorText}>Article not found</div>
          <button onClick={onBack} className={styles.errorButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const categoryDisplay = getCategoryDisplay(article.category);

  return (
    <div className={styles.detailView}>
      <div className={styles.detailHeader}>
        <button onClick={onBack} className={styles.backButton}>
          ← Back
        </button>
        <div className={styles.categoryBadge}>
          {categoryDisplay}
        </div>
      </div>

      <div className={styles.detailContent}>
        <h1 className={styles.detailTitle}>{article.title}</h1>

        {article.excerpt && (
          <div className={styles.detailDescription}>{article.excerpt}</div>
        )}

        {article.heroImage && (
          <div className={styles.detailImageContainer}>
            <img src={article.heroImage} alt={article.title} className={styles.detailImage} />
          </div>
        )}

        {article.content && (
          <div className={styles.detailBody}>
            <MarkdownRenderer content={article.content} />
          </div>
        )}

        {article.created_at && (
          <div className={styles.detailMeta}>
            Published: {new Date(article.created_at).toLocaleDateString()}
          </div>
        )}

        <div className={styles.bottomBackButton}>
          <button onClick={onBack}>
            Back to Articles
          </button>
        </div>
      </div>
    </div>
  );
}

export default ArticleDetailView;
